import { useRef, useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { setCredentials } from './authSlice'
import { useLoginMutation } from './authApiSlice'
import usePersist from '../../hooks/usePersist'
import useTitle from '../../hooks/useTitle'
import PulseLoader from 'react-spinners/PulseLoader'
import { useTranslation } from 'react-i18next'
import AuthShell from '../../components/AuthShell'
import PasswordField from '../../components/PasswordField'
import GoogleSignIn from './GoogleSignIn'

const Login = () => {
    const { t } = useTranslation()
    useTitle(t('titles.login'))

    const userRef = useRef()
    const errRef = useRef()
    const [username, setUsername] = useState('')
    const [password, setPassword] = useState('')
    const [errMsg, setErrMsg] = useState('')
    const [persist, setPersist] = usePersist()

    const navigate = useNavigate()
    const dispatch = useDispatch()

    const [login, { isLoading }] = useLoginMutation()

    useEffect(() => {
        userRef.current.focus()
    }, [])

    useEffect(() => {
        setErrMsg('')
    }, [username, password])

    const handleSubmit = async (e) => {
        e.preventDefault()
        try {
            const { accessToken } = await login({ username, password }).unwrap()
            dispatch(setCredentials({ accessToken }))
            setUsername('')
            setPassword('')
            navigate('/dash')
        } catch (err) {
            if (!err.status) {
                setErrMsg(t('auth.errors.noServerResponse'))
            } else if (err.status === 400) {
                setErrMsg(t('auth.errors.missingCredentials'))
            } else if (err.status === 401) {
                setErrMsg(t('auth.errors.unauthorized'))
            } else {
                setErrMsg(err.data?.message)
            }
            errRef.current.focus()
        }
    }

    const handleUserInput = (e) => setUsername(e.target.value)
    const handlePwdInput = (e) => setPassword(e.target.value)
    const handleToggle = () => setPersist(prev => !prev)

    const errClass = errMsg ? 'errmsg' : 'offscreen'

    if (isLoading) return <PulseLoader color={'#e8c56d'} />

    return (
        <AuthShell
            eyebrow={t('auth.eyebrow')}
            title={t('auth.signInHeading')}
            subtitle={t('auth.signInSubtitle')}
            links={[
                { to: '/forgot-password', label: t('auth.forgotPassword') },
                { to: '/register', label: t('auth.createAnAccount') },
                { to: '/', label: t('auth.backToHome') },
            ]}
        >
            <p ref={errRef} className={errClass} aria-live="assertive" tabIndex={-1}>{errMsg}</p>

            <form className="form" onSubmit={handleSubmit}>
                <div className="auth__field">
                    <label className="form__label" htmlFor="username">{t('auth.username')}</label>
                    <input
                        className="form__input"
                        type="text"
                        id="username"
                        ref={userRef}
                        value={username}
                        onChange={handleUserInput}
                        autoComplete="username"
                        required
                    />
                </div>

                <PasswordField
                    id="password"
                    label={t('auth.password')}
                    value={password}
                    onChange={handlePwdInput}
                />

                <button className="form__submit-button">{t('auth.signIn')}</button>

                <label className="auth__check" htmlFor="persist">
                    <input
                        type="checkbox"
                        id="persist"
                        onChange={handleToggle}
                        checked={persist}
                    />
                    <span className="auth__check-box" aria-hidden="true" />
                    {t('auth.trustDevice')}
                </label>
            </form>

            <GoogleSignIn onError={(msg) => { setErrMsg(msg); errRef.current?.focus() }} />
        </AuthShell>
    )
}

export default Login
